import React, { useState, useEffect } from 'react';
import useDataService from '../../hooks/useDataService';
import { eMessageType } from '../../types/IMessageType';
import { fetchData } from '../../_lib/lib';
import apiToUrlMap from '../../ApiMapping';
import { IFederatedLoginForm } from './types';
import { INITIAL_FORM_DATA, validateForm, buildPayload, extractLoginUrl } from './utils';

const useFederatedLogin = () => {
  const { openSnackBar } = useDataService();
  const [formData, setFormData] = useState<IFederatedLoginForm>(INITIAL_FORM_DATA);
  const [showApiKey, setShowApiKey] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginUrl, setLoginUrl] = useState<string | null>(null);

  useEffect(() => {
    try {
      const config = JSON.parse(localStorage.getItem('PxConfig') || '{}');
      document.title = `${config.siteTitle || 'PhoneX'} - Federated Login`;
    } catch {
      document.title = 'PhoneX - Federated Login';
    }
    return () => setFormData(INITIAL_FORM_DATA);
  }, []);

  const handleFieldChange = (field: keyof IFederatedLoginForm) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const trimmedVal = e.target.value.trim();
    const val = field === 'summary' ? trimmedVal.slice(0, 250) : trimmedVal;
    setFormData((prev) => ({ ...prev, [field]: val }));
  };

  const generateUrl = async () => {
    if (!validateForm(formData)) return openSnackBar('Please fill in all fields.', eMessageType.error);
    setLoading(true);
    setLoginUrl(null);
    try {
      const res: any = await fetchData('POST', apiToUrlMap.federatedLogin, { body: buildPayload(formData) });
      const url = !res?.error ? extractLoginUrl(res) : null;
      if (url) {
        setLoginUrl(url);
        openSnackBar('Federated login URL generated successfully!', eMessageType.success);
      } else {
        openSnackBar(res?.message || 'Failed to generate URL.', eMessageType.error);
      }
    } catch (err: any) {
      openSnackBar(err?.message || 'An error occurred.', eMessageType.error);
    } finally {
      setLoading(false);
    }
  };

  return {
    formData,
    showApiKey,
    setShowApiKey,
    loading,
    loginUrl,
    isValid: validateForm(formData),
    handleFieldChange,
    generateUrl,
    handleReset: () => setLoginUrl(null),
  };
};

export default useFederatedLogin;
